import { mixins } from "vue-class-component";
import { Component, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BCol, BForm, BRow, BSpinner } from "@/lib/typed-bootstrap";

import ErrorHandling from "@/mixins/ErrorHandling.mixin";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { IOrgModuleState, OrgModule } from "@/store/modules/org";
import FormInputWithLabel from "../forms/inputs/FormInputWithLabel.component";
import UpdateOrg from "./UpdateOrg.mixin";

import _isEqual from "lodash/isEqual";

export interface IOrgInputState {
  id: string;
  name: string;
}

export const mapOrgStateToInput = (state: IOrgModuleState): IOrgInputState => ({
  id: state.id,
  name: state.name ?? "",
});

@Component
export default class OrgDataForm extends mixins(
  UpdateOrg,
  UserPermissions,
  ErrorHandling,
) {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  $refs!: {
    form: HTMLFormElement;
  };

  form: any = null;
  input: IOrgInputState = mapOrgStateToInput(OrgModule.synth());
  confirmName = "";
  isLoading = false;

  get showCancelSaveButton(): boolean {
    if (!this.canManageOrg) return false;

    const newInput = mapOrgStateToInput(OrgModule.synth());
    return !_isEqual(newInput, this.input);
  }

  get orgId() {
    return OrgModule.id;
  }

  @Watch("orgId", { immediate: true })
  onOrgChange() {
    this.input = mapOrgStateToInput(OrgModule.synth());
  }

  handleCancelSettingsChange() {
    this.input = mapOrgStateToInput(OrgModule.synth());
  }

  async handleSaveSettingsChange(_ev: Event) {
    _ev.preventDefault();
    try {
      if (!this.input.name) {
        throw new Error(this.$t("error_org_requires_name").toString());
      }

      this.isLoading = true;
      await this.updateOrg(() => ({
        id: this.input.id,
        name: this.input.name,
      }));

      if (!this.errorMessage) {
        this.input = mapOrgStateToInput(OrgModule.synth());
      }
    } catch (err) {
      this.errorMessage = (err as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return (
      <div>
        <BAlert
          show={this.showErrorMessage}
          variant="danger"
          data-testid="error-alert"
        >
          {this.errorMessage}
        </BAlert>

        <BRow class="default__section">
          <div class="default__section-header">{this.$t("org_data_title")}</div>
        </BRow>

        <BRow class="default__section">
          <BForm
            ref="form"
            class="default__inline-form"
            acceptCharset="UTF-8"
            role="form"
            novalidate
            onSubmit={this.handleSaveSettingsChange}
          >
            <BRow cols="1" colsMd="2">
              <BCol>
                <FormInputWithLabel
                  value={this.input.id}
                  readonly={true}
                  label={this.$t("form_input_label_account_id").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_account_id",
                  ).toString()}
                  onInput={() => {}} // No editing on ID field
                />
              </BCol>

              <BCol>
                <FormInputWithLabel
                  value={this.input.name}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_workspace_name").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_workspace_name",
                  ).toString()}
                  inputType="name"
                  onInput={(newVal) => (this.input.name = newVal as string)}
                  onEscapeEditing={this.handleCancelSettingsChange}
                />
              </BCol>
            </BRow>

            {this.showCancelSaveButton && (
              <BRow class="buttons__section">
                <button
                  class="action-button__pill--outline action-button__flex-props"
                  type="reset"
                  onClick={(e) => {
                    e.preventDefault();
                    this.handleCancelSettingsChange();
                  }}
                >
                  {this.$t("global_cta_cancel")}
                </button>

                <button
                  class="action-button__pill--gradient action-button__flex-props"
                  type="submit"
                  data-testid="save-button"
                >
                  {this.isLoading ? (
                    <div
                      class="default__spinner--small"
                      role="status"
                      data-testid="loading-spinner"
                    >
                      <BSpinner
                        type="grow"
                        class="ml-auto"
                        label="Saving..."
                        small
                      />
                    </div>
                  ) : (
                    this.$t("global_cta_save")
                  )}
                </button>
              </BRow>
            )}
          </BForm>
        </BRow>
      </div>
    );
  }
}
